import {
  queueAdd,
  queueGetAll,
  queueUpdate,
  queueDelete,
  idMapGet,
  idMapSet,
} from "./offlineStore";
import { getAuthorizedCompanyContext } from "./companyContext";

let syncInProgress = false;
let onlineSyncHandler = null;

const toPositiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export function getOfflineSessionScope() {
  if (typeof localStorage === "undefined") {
    return { userId: null, companyId: null };
  }

  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    const userId = toPositiveInteger(userData?.id);
    const primaryCompanyId = toPositiveInteger(userData?.company_id);
    const selectedCompanyId = getAuthorizedCompanyContext();

    return {
      userId,
      companyId: selectedCompanyId || primaryCompanyId,
    };
  } catch {
    return { userId: null, companyId: null };
  }
}

export function isQueueEntryOwnedByCurrentUser(entry) {
  const { userId } = getOfflineSessionScope();
  const ownerUserId = toPositiveInteger(entry?.ownerUserId);
  return Boolean(userId && ownerUserId && userId === ownerUserId);
}

export async function getQueueEntriesForCurrentContext() {
  const { userId, companyId } = getOfflineSessionScope();
  if (!userId || !companyId) return [];

  const entries = await queueGetAll();
  return (entries || []).filter(
    (entry) =>
      toPositiveInteger(entry?.ownerUserId) === userId &&
      toPositiveInteger(entry?.companyContextId) === companyId
  );
}

/**
 * @typedef {Object} EnqueueRequestOptions
 * @property {string} method
 * @property {string} url
 * @property {unknown} [body]
 * @property {Record<string, string>} [headers]
 * @property {Record<string, unknown>} [meta]
 */

export function isOffline() {
  if (typeof navigator === "undefined") return false;
  return navigator.onLine === false;
}

export function buildTempId(prefix = "offline") {
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${now}-${rand}`;
}

/**
 * @param {EnqueueRequestOptions} [options={}]
 */
export async function enqueueRequest({
  method,
  url,
  body,
  headers = {},
  meta = {},
} = {}) {
  const { userId, companyId } = getOfflineSessionScope();
  if (!userId || !companyId) {
    throw new Error(
      "Session utilisateur ou contexte d'entreprise indisponible pour le mode hors ligne"
    );
  }

  const entry = {
    method: method.toUpperCase(),
    url,
    body: body ?? null,
    headers: {
      ...headers,
      "X-Company-Id": String(companyId),
    },
    ownerUserId: userId,
    companyContextId: companyId,
    meta,
    status: "pending",
    createdAt: Date.now(),
  };

  const id = await queueAdd(entry);
  return { id, entry };
}

function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

function getCompanyContextHeaders() {
  const { companyId } = getOfflineSessionScope();
  return companyId
    ? { "X-Company-Id": String(companyId) }
    : {};
}

function buildHeaders(entry) {
  return {
    "Content-Type": "application/json",
    ...getAuthHeaders(),
    ...getCompanyContextHeaders(),
    ...(entry.headers || {}),
  };
}

function replaceOrderIdInBody(body, orderId) {
  if (!body || !orderId) return body;
  if (body.order && body.order.id) {
    return {
      ...body,
      order: {
        ...body.order,
        id: orderId,
      },
    };
  }
  return body;
}

export async function processQueue({ onItemSuccess, onItemError } = {}) {
  if (syncInProgress || isOffline()) return { processed: 0 };

  const authToken = localStorage.getItem("authToken");
  const initialScope = getOfflineSessionScope();
  if (!authToken || !initialScope.userId) return { processed: 0 };

  syncInProgress = true;

  try {
    const items = await queueGetAll();
    const sorted = [...items].sort((a, b) => a.createdAt - b.createdAt);
    let processed = 0;

    for (const entry of sorted) {
      if (!entry || entry.status === "processing") continue;
      if (!isQueueEntryOwnedByCurrentUser(entry)) continue;

      const dependsOn = entry.meta?.dependsOnTempId;
      if (dependsOn) {
        const mappedId = await idMapGet(dependsOn);
        if (!mappedId) {
          continue;
        }
        entry.body = replaceOrderIdInBody(entry.body, mappedId);
      }

      await queueUpdate({ ...entry, status: "processing" });

      try {
        // La session peut changer pendant une synchronisation longue. Ne pas
        // executer une action avec les identifiants d'un autre utilisateur.
        if (
          !isQueueEntryOwnedByCurrentUser(entry) ||
          !localStorage.getItem("authToken")
        ) {
          await queueUpdate({ ...entry, status: "pending" });
          continue;
        }

        const response = await fetch(entry.url, {
          method: entry.method,
          headers: buildHeaders(entry),
          body: entry.body ? JSON.stringify(entry.body) : undefined,
        });

        if (!response.ok) {
          const errorText = await response.text();
          await queueUpdate({
            ...entry,
            status: "failed",
            lastError: errorText || response.statusText,
          });
          if (onItemError) onItemError(entry, errorText);
          continue;
        }

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (entry.meta?.tempId) {
          const serverId =
            payload?.orderId || payload?.id || payload?.order?.id;
          if (serverId) {
            await idMapSet(entry.meta.tempId, serverId);
          }
        }

        await queueDelete(entry.id);
        processed += 1;

        if (onItemSuccess) onItemSuccess(entry, payload);
      } catch (error) {
        await queueUpdate({
          ...entry,
          status: "failed",
          lastError: error?.message || "sync failed",
        });
        if (onItemError) onItemError(entry, error);
      }
    }

    return { processed };
  } finally {
    syncInProgress = false;
  }
}

export function startQueueSync(options = {}) {
  if (typeof window === "undefined") return () => {};
  const { onItemSuccess, onItemError, immediate } = options;

  const handler = () => {
    processQueue({ onItemSuccess, onItemError });
  };

  if (onlineSyncHandler) {
    window.removeEventListener("online", onlineSyncHandler);
  }

  onlineSyncHandler = handler;
  window.addEventListener("online", handler);

  if (immediate) {
    handler();
  }

  return () => {
    window.removeEventListener("online", handler);
    if (onlineSyncHandler === handler) {
      onlineSyncHandler = null;
    }
  };
}
